import React, { useEffect, useState, useRef } from "react";
import Prismic from "@prismicio/client";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import New from "../Assets/new.json";
import Lock from "../Assets/lock.svg";

const VIDEO_EXTENSION_PATTERN = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

// The `outcome` field is currently just an empty text field on every
// worklist item — repurposed here to optionally hold an image or video
// URL. Hovering the card reveals it over the static thumbnail; if it's
// empty (the common case today), nothing renders and the card behaves
// exactly as before.
function WorkOutcomeMedia({ url, isHovered, alt }) {
  if (!url) return null;
  const isVideo = VIDEO_EXTENSION_PATTERN.test(url);

  return isVideo ? (
    <video
      className={`outcome ${isHovered ? "visible" : ""}`}
      src={url}
      autoPlay
      muted
      loop
      playsInline
    />
  ) : (
    <img className={`outcome ${isHovered ? "visible" : ""}`} src={url} alt={alt || ""} />
  );
}

function MultiCaseOptionCard({ link, fallbackUrl, title, description, image, onNavigate }) {
  const isDocument = link?.link_type === "Document" && link?.uid;
  if (!isDocument && !fallbackUrl) return null;

  const cardContent = (
    <>
    
      <img className="multi-case-card-image" src={image} alt={title} />
      <div className="multi-case-card-footer">
        <div className="multi-case-card-tag">
          <span className="multi-case-card-dot" />
          <span>{title}</span>
        </div>
        <h3 className="multi-case-card-heading">{description}</h3>
      </div>
    </>
  );

  return isDocument ? (
    // Content relationship (linked document): open the internal case-study page
    <Link
      to={`/case-study/${link.uid}`}
      className="multi-case-card"
      onClick={onNavigate}
    >
      {cardContent}
    </Link>
  ) : (
    // Not a content relationship: fall back to the plain link_1/link_2 URL
    <a
      href={fallbackUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="multi-case-card"
      onClick={onNavigate}
    >
      {cardContent}
    </a>
  );
}

export default function Work() {
  //Multi case-study picker modal
  const [isCaseModalOpen, setIsCaseModalOpen] = useState(false);
  const [activeWorkItem, setActiveWorkItem] = useState(null);

  const openMultiCaseModal = (workItem) => {
    setActiveWorkItem(workItem);
    setIsCaseModalOpen(true);
  };

  const closeMultiCaseModal = () => {
    setIsCaseModalOpen(false);
    setActiveWorkItem(null);
  };

  useEffect(() => {
    document.body.style.overflow = isCaseModalOpen ? "hidden" : "";
    document.documentElement.style.overflow = isCaseModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isCaseModalOpen]);

  // "View Case Study" cursor pill: follows the mouse via direct DOM updates
  // (not React state) so it doesn't trigger a re-render on every mousemove.
  const cursorPillRef = useRef(null);
  const [isCursorPillVisible, setIsCursorPillVisible] = useState(false);
  // Which card's outcome image/video overlay (if it has one) is showing.
  const [hoveredWorkId, setHoveredWorkId] = useState(null);

  const handleWorkMouseMove = (evt) => {
    if (cursorPillRef.current) {
      cursorPillRef.current.style.transform = `translate(${evt.clientX}px, ${evt.clientY}px) translate(-50%, -50%)`;
    }
  };

  // mouseleave never fires when a card scrolls out from under a stationary
  // cursor — only actual pointer movement triggers it. Hide on scroll too.
  useEffect(() => {
    const hidePillOnScroll = () => setIsCursorPillVisible(false);
    window.addEventListener("scroll", hidePillOnScroll, { passive: true });
    return () => window.removeEventListener("scroll", hidePillOnScroll);
  }, []);

  const apiEndpoint = "https://harshitsharma.prismic.io/api/v2";
  const accessToken =
    "MC5ZYUVtbXhNQUFETUFuODZN.77-977-9Ye-_ve-_vX8o77-977-977-977-977-9a--_ve-_ve-_ve-_ve-_ve-_vQITBF5h77-977-977-977-977-977-9TO-_vQ";
  // This is where you would add your access token for a Private repository
  const Client = Prismic.client(apiEndpoint, { accessToken });
  const [doc, setDoc] = useState([]);
  const [isWorkLoading, setIsWorkLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const response = await Client.query(
        Prismic.Predicates.at("document.type", "worklist"),
        { orderings: "[my.worklist.order desc]" },
      );
      if (response) {
        setDoc(response.results);
      }
      setIsWorkLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container">
      <div
        ref={cursorPillRef}
        className={`work-cursor-pill ${isCursorPillVisible ? "visible" : ""}`}
      >
        View Case Study
      </div>

      <div className="container-div1">
        <h1>Case Studies with Results</h1>
        <p>
          Below projects showcase my EDPIT design process, iterations, final
          product, including results & achievements.
        </p>
      </div>

      <div className="work-container" onMouseMove={handleWorkMouseMove}>
        {isWorkLoading &&
          Array.from({ length: 4 }).map((_, i) => (
            <div className="work-div work-div-skeleton" key={`skeleton-${i}`}>
              <div className="skeleton" style={{ width: "100%", height: 280 }} />
              <div className="work-header">
                <div className="work-header-left">
                  <div className="skeleton skeleton-circle" style={{ width: 12, height: 12 }} />
                  <div className="skeleton skeleton-text" style={{ width: "60%" }} />
                </div>
                <div className="skeleton skeleton-text" style={{ width: 40 }} />
              </div>
              <div className="desc-div">
                <div className="skeleton skeleton-text" style={{ width: "90%", marginBottom: 6 }} />
                <div className="skeleton skeleton-text" style={{ width: "70%" }} />
              </div>
            </div>
          ))}

        {!isWorkLoading &&
          doc?.map((e) => {
          const multiCaseStudies = e?.data?.multi_case_studies?.[0];
          const workId = e?.id || e?.data?.title;
          const outcomeUrl = e?.data?.outcome;
          const isOutcomeHovered = hoveredWorkId === workId;

          return (
          <div
            className="work-div"
            key={workId}
            style={{
              backgroundColor: e?.data?.work_color || "#2b2b2b", // fallback dark grey if not set in Prismic
              "--work-color": e?.data?.work_color || "rgb(43, 43, 43)",
            }}
            onMouseEnter={() => {
              setIsCursorPillVisible(true);
              setHoveredWorkId(workId);
            }}
            onMouseLeave={() => {
              setIsCursorPillVisible(false);
              setHoveredWorkId(null);
            }}
          >
            {e?.data?.new ? (
              <Lottie className="nda" animationData={New} loop={true} />
            ) : (
              <div className="work-card-lock">
                <img className="lock-icon" src={Lock} alt="" />
              </div>
            )}

            {e?.data?.nda ? (
              // NDA project (not clickable)
              <>
                <div className="work-thumb-wrap">
                  <img
                    className="thumbnail"
                    src={e?.data?.images?.[0]?.image_link}
                    alt={e?.data?.title || ""}
                  />
                  <WorkOutcomeMedia
                    url={outcomeUrl}
                    isHovered={isOutcomeHovered}
                    alt={e?.data?.title}
                  />
                </div>
                <div className="work-header">
                  <div className="work-header-left">
                    <div className="work-block" />
                    <h1 className="work-title">{e?.data?.title}</h1>
                  </div>
                  <span className="work-year">→ 2024 ←</span>
                </div>

                <div className="desc-div">
                  <p>{e?.data?.long_description}</p>
                </div>
              </>
            ) : e?.data?.onclicklink && e?.data?.link ? (
              // External link project
              <a href={e.data.link} target="_blank" rel="noopener noreferrer">
                <div className="work-thumb-wrap">
                  <img
                    className="thumbnail"
                    src={e?.data?.images?.[0]?.image_link}
                    alt={e?.data?.title || ""}
                  />
                  <WorkOutcomeMedia
                    url={outcomeUrl}
                    isHovered={isOutcomeHovered}
                    alt={e?.data?.title}
                  />
                </div>
                <div className="work-header">
                  <div className="work-header-left">
                    <div className="work-block" />
                    <h1 className="work-title">{e?.data?.title}</h1>
                  </div>
                  <span className="work-year">→ 2024 ←</span>
                </div>

                <div className="desc-div">
                  <p>{e?.data?.long_description}</p>
                </div>
              </a>
            ) : multiCaseStudies?.multi_case_studies1 ? (
              // Multiple case studies: opens a picker modal instead of navigating directly
              <div
                role="button"
                tabIndex={0}
                className="work-multi-trigger"
                onClick={() => openMultiCaseModal(e)}
                onKeyDown={(evt) => {
                  if (evt.key === "Enter" || evt.key === " ") {
                    openMultiCaseModal(e);
                  }
                }}
              >
                <div className="work-thumb-wrap">
                  <img
                    className="thumbnail"
                    src={e?.data?.images?.[0]?.image_link}
                    alt={e?.data?.title || ""}
                  />
                  <WorkOutcomeMedia
                    url={outcomeUrl}
                    isHovered={isOutcomeHovered}
                    alt={e?.data?.title}
                  />
                </div>
                <div className="work-header">
                  <div className="work-header-left">
                    <div className="work-block" />
                    <h1 className="work-title">{e?.data?.title}</h1>
                  </div>
                  <span className="work-year">→ 2024 ←</span>
                </div>

                <div className="desc-div">
                  <p>{e?.data?.long_description}</p>
                </div>
              </div>
            ) : (
              // Internal case-study link
              <Link to={`/case-study/${e?.data?.content_relationship?.uid}`}>
                <div className="work-thumb-wrap">
                  <img
                    className="thumbnail"
                    src={e?.data?.images?.[0]?.image_link}
                    alt={e?.data?.title || ""}
                  />
                  <WorkOutcomeMedia
                    url={outcomeUrl}
                    isHovered={isOutcomeHovered}
                    alt={e?.data?.title}
                  />
                </div>
                <div className="work-header">
                  <div className="work-header-left">
                    <div className="work-block" />
                    <h1 className="work-title">{e?.data?.title}</h1>
                  </div>
                  <span className="work-year">→ 2024 ←</span>
                </div>

                <div className="desc-div">
                  <p>{e?.data?.long_description}</p>
                </div>
              </Link>
            )}
          </div>
          );
        })}
      </div>

      <Modal
        open={isCaseModalOpen}
        onClose={closeMultiCaseModal}
        center
        showCloseIcon={false}
        blockScroll={false}
        classNames={{
          modal: "multi-case-modal",
          modalAnimationIn: "multi-case-slide-in",
          modalAnimationOut: "multi-case-slide-out",
        }}
      >
        <div className="multi-case-modal-topbar">
          <h2 className="multi-case-modal-heading">Choose case study to view</h2>
          <button
            type="button"
            className="multi-case-modal-close"
            onClick={closeMultiCaseModal}
          >
            <span className="multi-case-modal-close-dot" />
            Close
          </button>
        </div>
        <div className="multi-case-modal-grid">
          {(() => {
            const multi = activeWorkItem?.data?.multi_case_studies?.[0];
            return (
              <>
                <MultiCaseOptionCard
                  link={multi?.case_study_link_1}
                  fallbackUrl={multi?.link_1}
                  title={multi?.title_1}
                  description={multi?.description_1}
                  image={multi?.image_1}
                  onNavigate={closeMultiCaseModal}
                />
                <MultiCaseOptionCard
                  link={multi?.case_study_link_2}
                  fallbackUrl={multi?.link_2}
                  title={multi?.title_2}
                  description={multi?.description_2}
                  image={multi?.image_2}
                  onNavigate={closeMultiCaseModal}
                />
              </>
            );
          })()}
        </div>
      </Modal>
    </div>
  );
}
